import React from 'react';
import { Box, Container, Typography, Grid, Link } from '@mui/material';
import { COLORS } from '../../utils/colors';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

// A footer link is either a real route (renders as a link) or a not-yet-built
// item (renders as plain, non-clickable text — never a dead href="#").
interface FooterItem {
  label: string;
  href?: string; // present = live route; absent = placeholder text
}

const COLUMNS: { heading: string; items: FooterItem[] }[] = [
  {
    heading: 'Product',
    items: [
      { label: 'Demo', href: '/demo' },
      { label: 'Plans', href: '/#plans' },
      { label: 'Early Access', href: '/early-access' },
      { label: 'Features' },
      { label: 'Mobile App' },
    ],
  },
  {
    heading: 'Company',
    items: [
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Careers' },
      { label: 'Press' },
    ],
  },
  {
    heading: 'Resources',
    items: [
      { label: 'Blog & Resources', href: '/resources' },
      { label: 'Help Center / FAQ', href: '/faq' },
      { label: 'Community' },
      { label: 'Status' },
    ],
  },
  {
    heading: 'Legal',
    items: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Use', href: '/terms' },
      { label: 'Acceptable Use', href: '/aup' },
      { label: 'Cookie Policy', href: '/cookie' },
      { label: 'Data Deletion', href: '/data-deletion' },
      { label: 'DMCA', href: '/dmca' },
    ],
  },
];

const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const { openManage } = useCookieConsent();

  const itemSx = {
    color: 'rgba(255,255,255,0.82)',
    fontSize: '0.85rem',
    lineHeight: 2,
  };
  const linkSx = {
    ...itemSx,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { color: '#fff', textDecoration: 'underline' },
    transition: 'color 0.2s',
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: COLORS.ink,
        color: '#fff',
        pt: { xs: 5, md: 7 },
        pb: { xs: 4, md: 5 },
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 2 }}>
          {/* Brand */}
          <Grid item xs={12} md={3}>
            <Typography
              component="a"
              href="/"
              sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#fff', textDecoration: 'none', letterSpacing: 0.5 }}
            >
              PERCORO
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.72)', mt: 1.5, maxWidth: 260 }}>
              The social travel platform for documenting trips, sharing photos, and planning your next adventure with AI.
            </Typography>
          </Grid>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <Grid item xs={6} md={2.25} key={col.heading}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1.25, color: '#fff' }}>
                {col.heading}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {col.items.map((item) =>
                  item.href ? (
                    <Link key={item.label} href={item.href} sx={linkSx}>
                      {item.label}
                    </Link>
                  ) : (
                    <Typography key={item.label} component="span" sx={itemSx}>
                      {item.label}
                    </Typography>
                  )
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Divider */}
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.18)', my: { xs: 2.5, md: 3 } }} />

        {/* Bottom strip */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' }}>
            © {year} Percoro. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
            <Link href="/privacy" sx={{ ...linkSx, lineHeight: 1.5 }}>Privacy</Link>
            <Link href="/terms" sx={{ ...linkSx, lineHeight: 1.5 }}>Terms</Link>
            <Link href="/cookie" sx={{ ...linkSx, lineHeight: 1.5 }}>Cookies</Link>
            <Link component="button" type="button" onClick={openManage} sx={{ ...linkSx, lineHeight: 1.5, background: 'none', border: 'none', p: 0, cursor: 'pointer' }}>Manage cookies</Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
